/**
 * Live counts for the classic chrome: a menu title (`Alerts (3)`) or a
 * toolbar badge (Tasks) names a counter id; the screen or module that knows
 * the number registers it. Owner: WP0-SHELL (integrator).
 *
 *   useCounter('alerts.unread', count)   while mounted (a module or a mode's shell.global slot)
 *   useCounterValue('alerts.unread')     what the chrome reads (0 when nobody registered it)
 */
import { useEffect, useSyncExternalStore } from 'react';

const values = new Map<string, number>();
const listeners = new Set<() => void>();
let version = 0;

function emit(): void {
  version += 1;
  for (const l of listeners) l();
}

/** Sets a counter; returns a function that clears it. */
export function registerCounter(id: string, value: number): () => void {
  values.set(id, value);
  emit();
  return () => {
    if (values.get(id) === value) {
      values.delete(id);
      emit();
    }
  };
}

export function counterValue(id: string): number {
  return values.get(id) ?? 0;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Publishes `value` under `id` for as long as the caller is mounted. */
export function useCounter(id: string, value: number): void {
  useEffect(() => registerCounter(id, value), [id, value]);
}

/** The current value of a counter (re-renders when any counter changes). */
export function useCounterValue(id: string | undefined): number {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
  return id ? counterValue(id) : 0;
}

/** Test hook. */
export function resetCounters(): void {
  values.clear();
  emit();
}
